import * as R from "ramda";
import React from "react";
import { Redirect } from "react-router";
import { css } from "glamor";
import { Divider, Icon, Row } from "antd";
import Table from "@arachnys/styleguide/Table";
import Heading from "@arachnys/styleguide/Heading";
import { SHOULD_SHOW_EXTENDED_ENTITY_SCHEMA } from "@arachnys/flow-app/src/config";
import { formatDatetimeString } from "@arachnys/flow-app/src/formatters";
import Country from "../../../component/Country";
import authFetch from "../../../api/authFetch";
import RelatedEntitiesViewerButton from "./ButtonContainer";
import RelatedEntitiesActionButtons from "./RelatedEntitiesActionButtons";

// TODO should probably send a number over the GraphQL API
const arachnysDecimalToNumber = ({ exponent, mantissa }) =>
    (mantissa * 10 ** exponent).toFixed(2);

const styles = {
    iconBlue: css({
        color: "#1b75bc",
        paddingLeft: "22px",
    }),
    iconRed: css({
        color: "#d0021b",
        paddingLeft: "22px",
    }),
    noData: css({
        visibility: "hidden",
    }),
    theadTh: css({
        textAlign: "left",
    }),
};

const relatedEntitiesColumns = (type, entity, relationLabels) => {
    let columns = [
        {
            Header: "ULID",
            id: "entityULID",
            accessor: "key.ulid",
            show: false,
        },
        {
            accessor: "name",
            Header: "Entity Name",
            // TODO: replace <a> with <Link> in entity name column
            Cell: row => (
                <div>
                    {row.value}
                    <a href={`/beta/entities/${row.row.entityULID}`}>
                        <Icon type="solution" {...styles.iconBlue} />
                    </a>
                </div>
            ),
        },
        {
            id: "countryCode",
            accessor: "legalAddress.countryCode",
            Header: "Country",
            Cell: row => <Country.Name>{row.value}</Country.Name>,
        },
        {
            accessor: "dateOfBirth",
            Header: "Date of Birth",
            Cell: row => formatDatetimeString({ str: row.value }),
        },
        {
            id: "relationType",
            accessor: ({ relationType }) =>
                relationLabels[relationType]
                    ? relationLabels[relationType]
                    : relationType,
            Header: "Relation Type",
        },
    ];

    if (SHOULD_SHOW_EXTENDED_ENTITY_SCHEMA) {
        columns = [
            ...columns,
            {
                id: "role",
                accessor: "role",
                Header: "Role",
            },
            {
                id: "votingRightsPercentage",
                accessor: ({ votingRightsPercentage }) =>
                    votingRightsPercentage
                        ? arachnysDecimalToNumber(votingRightsPercentage)
                        : "",
                Header: "Voting Rights %",
            },
            {
                id: "shareType",
                accessor: "shareType",
                Header: "Share Type",
            },
        ];
    }

    const shareholdingColumn = {
        id: "shareholding",
        accessor: ({ shareholdingPercentage }) => {
            if (!shareholdingPercentage) return "Unknown";
            const percentage = arachnysDecimalToNumber(shareholdingPercentage);
            return percentage ? `${percentage}%` : "Unknown";
        },
        Header: "Shareholding",
    };

    const actionColumn = {
        id: "action",
        Header: "",
        accessor: r => (
            <RelatedEntitiesActionButtons
                mainEntity={entity}
                relatedEntity={r}
            />
        ),
    };

    switch (type) {
        case "boardsAndCommittees":
            return [...columns, {}, actionColumn];
        case "shareholders":
            return [...columns, shareholdingColumn, actionColumn];
    }
};

const requestTypeGroupsMap = {
    BRANCH_OF: "affiliatedEntity",
    SISTER_COMPANY_OF: "affiliatedEntity",
    SUBSIDIARY_OF: "affiliatedEntity",
    DIRECTOR_OF: "executive",
    SIGNATORY_OF: "executive",
    EXECUTIVE_OF: "executive",
    PARENT_COMPANY_OF: "directAndIndirectEntity",
    SHAREHOLDER_OF: "directAndIndirectEntity",
    UBO_OF: "directAndIndirectEntity",
};

const determineGroup = relationType =>
    requestTypeGroupsMap[relationType] || "other";

const RelationTable = ({
    entity,
    relationLabels,
    heading,
    data,
    commonProps,
    columnsType = "boardsAndCommittees",
}) =>
    data && data.length ? (
        <>
            <Row>
                <Heading level={1}>{heading}</Heading>
            </Row>
            <Row>
                <Table
                    columns={relatedEntitiesColumns(
                        columnsType,
                        entity,
                        relationLabels
                    )}
                    data={data}
                    {...commonProps}
                />
            </Row>
        </>
    ) : null;
const getTheadThProps = () => ({
    className: `${styles.theadTh}`,
});

const getNoDataProps = () => ({
    className: `${styles.noData}`,
});

export default class RelatedEntitiesViewer extends React.Component {
    state = {
        nextTaskId: null,
        relationLabels: this.props.schema.properties.type.options.reduce(
            (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
            {}
        ),
    };

    startProcess = async link => {
        const ulid = this.props.entity.key.ulid;
        const body = JSON.stringify({
            ulid,
        });
        const request = new Request(link, {
            method: "POST",
            body,
        });
        const response = await authFetch(request);
        const jsonRes = await response.json();

        this.setState({ nextTaskId: jsonRes.next_task_id });
    };

    handleClick = (_, process) => this.startProcess(process.link);

    renderRedirect = () => {
        const { nextTaskId } = this.state;
        return nextTaskId ? (
            <Redirect push to={`/tasks/${nextTaskId}/claim/`} />
        ) : null;
    };

    render() {
        const { relations } = this.props.entity;

        const groupByRelationType = R.groupBy(({ type: relationType }) =>
            determineGroup(relationType)
        );

        const relationToEntityAndType = rel => {
            return {
                ...rel.fromEntity,
                relationType: rel.type,
                role: rel.role,
                shareholdingPercentage: rel.shareholdingPercentage,
                shareType: rel.shareType,
                votingRightsPercentage: rel.votingRightsPercentage,
            };
        };

        const {
            executive,
            affiliatedEntity,
            directAndIndirectEntity,
            other,
        } = R.pipe(
            groupByRelationType,
            R.map(R.map(relationToEntityAndType))
        )(relations);

        const commonProps = {
            minRows: 0,
            showPagination: true,
            getTheadThProps,
            getNoDataProps,
        };

        return (
            <React.Fragment>
                {this.renderRedirect()}
                <Row>
                    <RelatedEntitiesViewerButton onClick={this.handleClick} />
                </Row>
                <RelationTable
                    entity={this.props.entity}
                    relationLabels={this.state.relationLabels}
                    heading="Executive"
                    data={executive}
                    commonProps={commonProps}
                />
                {executive && executive.length && (
                    <Row>
                        <Divider />
                    </Row>
                )}
                <RelationTable
                    entity={this.props.entity}
                    relationLabels={this.state.relationLabels}
                    heading="Affiliated entities"
                    data={affiliatedEntity}
                    commonProps={commonProps}
                />
                {affiliatedEntity && affiliatedEntity.length && (
                    <Row>
                        <Divider />
                    </Row>
                )}
                <RelationTable
                    entity={this.props.entity}
                    relationLabels={this.state.relationLabels}
                    heading="Direct and indirect entities"
                    data={directAndIndirectEntity}
                    commonProps={commonProps}
                    columnsType="shareholders"
                />
                {directAndIndirectEntity && directAndIndirectEntity.length && (
                    <Row>
                        <Divider />
                    </Row>
                )}
                <RelationTable
                    entity={this.props.entity}
                    relationLabels={this.state.relationLabels}
                    heading="Other"
                    data={other}
                    commonProps={commonProps}
                />
            </React.Fragment>
        );
    }
}
